import { Input, Select } from "@clintonelec/react-storybook";
import {
	invalidIpValidityMessage, ipNetworkSettingsWarningMessage, NetworkType, networkTypeSelectOptions
} from "Data/Objects/Network";
import { type Dispatch, Fragment, memo, type SetStateAction } from "react";
import type { NetworkIpSettings } from "Interfaces";
import { ipv6Regex } from "@clintonelec/typescriptutils";
import { NotificationType, notify } from "src/Notifications";
import { produce } from "immer";

type Ipv6SetupSettingsProps = {
	ipSetupSettings: NetworkIpSettings;
	updateIpv6NetworkType: Dispatch<SetStateAction<NetworkType>>;
	updateLinkLocalAddress: Dispatch<SetStateAction<string>>;
	updateIpv6Address: Dispatch<SetStateAction<string>>;
	updatePrefixLength: (prefixLength: string) => void;
	updateIpv6Gateway: Dispatch<SetStateAction<string>>;
	updateDnsIpv6: (dnsIpv6: string[]) => void;
};

function Ipv6SetupSettings(props: Ipv6SetupSettingsProps) {
	const {
		ipSetupSettings: { ipv6Settings, ipv6Settings: { ipv6NetworkType } }, updateIpv6NetworkType,
		updateLinkLocalAddress: handleLinkLocalAddressUpdate, updateIpv6Address: handleIpv6AddressUpdate,
		updatePrefixLength: handlePrefixLengthUpdate, updateIpv6Gateway: handleIpv6GatewayUpdate,
		updateDnsIpv6
	} = props;

	const handleIpv6NetworkTypeSelect = (newIpv6NetworkType: string | null) => {
		if (!newIpv6NetworkType) {
			return;
		}

		if (newIpv6NetworkType === NetworkType.DHCP_AUTOMATIC_DNS) {
			notify(
				NotificationType.WARNING,
				"WARNING",
				ipNetworkSettingsWarningMessage,
				undefined,
				{ toastId: "ipv6SelectToast" }
			);
		}

		updateIpv6NetworkType(newIpv6NetworkType as NetworkType);
	};

	const handleUpdateDnsIpv6 = (index: number) => (newDnsIpv6: string) => {
		updateDnsIpv6(produce(ipv6Settings.dnsIpv6, draft => {
			draft[ index ] = newDnsIpv6;
		}));
	};

	const disabledInput = ipv6NetworkType !== NetworkType.MANUAL;
	const dnsAddressDisabled = ipv6NetworkType === NetworkType.OFF
		|| ipv6NetworkType === NetworkType.DHCP_AUTOMATIC_DNS;

	const dnsFields = (
		ipv6Settings.dnsIpv6.map((dns, index) => {
			return (
				// eslint-disable-next-line react/no-array-index-key
				<div className="form-row" key={ `dns-ipv6-${ index }` }>
					<label>DNS Address { index + 1 }</label>
					<Input
						disabled={ dnsAddressDisabled }
						name="dnsIpv6"
						onUpdate={ handleUpdateDnsIpv6(index) }
						value={ dns }
						validator={ ipv6Regex }
						validityMessage={ invalidIpValidityMessage }
						wrapClassName="form-input"
					/>
				</div>
			);
		})
	);

	return (
		<Fragment>
			<div className="form-row">
				<label>IPv6 Network Type</label>
				<Select
					allowClear={ false }
					className="form-select"
					name="ipv6NetworkType"
					onSelect={ handleIpv6NetworkTypeSelect }
					options={ networkTypeSelectOptions }
					value={ ipv6NetworkType }
				/>
			</div>
			<div className="form-row">
				<label>Link Local Address</label>
				<Input
					disabled={ true }
					name="linkLocalAddress"
					onUpdate={ handleLinkLocalAddressUpdate }
					validator={ ipv6Regex }
					value={ ipv6Settings.linkLocalAddress }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<label>IP Address</label>
				<Input
					disabled={ disabledInput }
					name="ipv6Address"
					onUpdate={ handleIpv6AddressUpdate }
					value={ ipv6Settings.ipv6Address }
					validator={ ipv6Regex }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<label>Prefix Length</label>
				<Input
					disabled={ disabledInput }
					hideCharMessage
					min={ 0 }
					max={ 128 }
					name="prefixLength"
					onUpdate={ handlePrefixLengthUpdate }
					type="number"
					value={ ipv6Settings.prefixLength }
					wrapClassName="form-input"
				/>
			</div>
			<div className="form-row">
				<label>Gateway Address</label>
				<Input
					disabled={ disabledInput }
					name="ipv6Gateway"
					onUpdate={ handleIpv6GatewayUpdate }
					validator={ ipv6Regex }
					value={ ipv6Settings.ipv6Gateway }
					validityMessage={ invalidIpValidityMessage }
					wrapClassName="form-input"
				/>
			</div>
			{ dnsFields }
		</Fragment>
	);
}

export default memo(Ipv6SetupSettings);
